// InvoiceItems.jsx
import React, { useState, useEffect, useMemo } from 'react';
import Sidebar from './Sidebar';
import {
  FaDownload,
  FaPlus,
  FaEdit,
  FaTrash,
  FaEye,
  FaSortUp,
  FaSortDown,
  FaChevronLeft,
  FaChevronRight,
} from 'react-icons/fa';
import DeliveryNoteFormModal from './DeliveryNoteFormModal';
import DeliveryNoteViewModal from './DeliveryNoteViewModal';
import PDFDesigner from './pdfDesigner';
import axios from 'axios';
import { useScreenPermissions } from './useScreenPermissions';
import { useNavigate } from 'react-router-dom';

const API_URL = '/api';

const InvoiceItems = () => {
  /* ---------- Theme (kept consistent with SalesOrder layout) ---------- */
  // Use blue-500 (#3b82f6) for active selected file bg, profile icon bg and logout bg (as requested)
  const pageTheme = useMemo(() => ({
    bg: '#f3f4f6', // tailwind gray-100 like
    sidebarBg: '#ffffff',
    accent: '#3b82f6', // blue-500 for active item / create button accent (requested)
    border: '#d1d5db', // gray-300
    text: '#0f1724',
    muted: '#6b7280',
    iconColor: '#1f2937',
    hoverBg: '#eff6ff',
    logoutBg: '#3b82f6', // profile & logout bg
  }), []);

  /* ---------- inject theme vars & sidebar overrides (runs unconditionally) ---------- */
  useEffect(() => {
    const themeVars = {
      '--sidebar-line-color': pageTheme.border,
      '--sidebar-bg': pageTheme.sidebarBg,
      '--sidebar-text': pageTheme.text,
      '--accent-color': pageTheme.accent,
      '--accent-hover': pageTheme.accent,
      '--page-border-color': pageTheme.border,
      '--page-bg': pageTheme.bg,
      '--text-primary': pageTheme.text,
      '--text-muted': pageTheme.muted,
      '--icon-color': pageTheme.iconColor,
      '--hover-bg': pageTheme.hoverBg,
      '--logout-bg': pageTheme.logoutBg,
    };

    Object.entries(themeVars).forEach(([k, v]) => document.documentElement.style.setProperty(k, v));

    const styleId = 'invoice-sidebar-overrides';
    const existing = document.getElementById(styleId);
    if (existing) existing.remove();

    const style = document.createElement('style');
    style.id = styleId;
    style.innerHTML = `
      /* Sidebar base: white background, dark text for non-active items */
      .sidebar, .app-sidebar { background-color: var(--sidebar-bg) !important; color: var(--sidebar-text) !important; }
      .sidebar a, .sidebar .menu-item, .app-sidebar a, .app-sidebar .menu-item,
      .sidebar .nav-link, .app-sidebar .nav-link { color: var(--sidebar-text) !important; background-color: #ffffff !important; }

      /* Active item: use accent (blue-500) background with white text & icons */
      .sidebar a.active, .sidebar .nav-link.active, .sidebar .menu-item.active, .sidebar .nav-item.active,
      .app-sidebar a.active, .app-sidebar .nav-link.active {
        background-color: var(--accent-color) !important; /* blue-500 background */
        color: #ffffff !important; /* active text white and visible */
        font-weight: 700;
      }

      /* Force all nested text inside the active item to be white */
      .sidebar a.active *,
      .sidebar .menu-item.active *,
      .sidebar .nav-link.active *,
      .app-sidebar a.active *,
      .app-sidebar .menu-item.active *,
      .app-sidebar .nav-link.active * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Specific common label selectors some sidebars use */
      .sidebar a.active .menu-text,
      .sidebar a.active .menu-item-text,
      .sidebar a.active .item-label,
      .sidebar .menu-item.active .menu-text,
      .sidebar .menu-item.active .menu-item-text,
      .sidebar .menu-item.active .item-label {
        color: #ffffff !important;
        opacity: 1 !important;
      }

      /* Ensure active icons also use white so they are visible on blue background */
      .sidebar a.active svg, .sidebar .nav-link.active svg, .app-sidebar a.active svg {
        color: #ffffff !important;
        fill: #ffffff !important;
      }

      /* If sidebar implementation used an inset bar, keep it subtle or remove */
      .sidebar a.active::before, .app-sidebar a.active::before {
        background: transparent !important;
      }

      /* Catch-all: if sidebar uses Tailwind classes or inline blue colors, force white text for those elements */
      .sidebar .bg-blue-500, .sidebar .bg-blue-600,
      .app-sidebar .bg-blue-500, .app-sidebar .bg-blue-600,
      .sidebar [class*="bg-blue-500"], .sidebar [class*="bg-blue-600"],
      .app-sidebar [class*="bg-blue-500"], .app-sidebar [class*="bg-blue-600"],
      .sidebar [style*="#3b82f6"], .sidebar [style*="#2563eb"],
      .app-sidebar [style*="#3b82f6"], .app-sidebar [style*="#2563eb"],
      .sidebar [style*="background-color: var(--accent-color)"], .app-sidebar [style*="background-color: var(--accent-color)"] {
        color: #ffffff !important;
      }

      .sidebar .bg-blue-500 *, .sidebar .bg-blue-600 *,
      .app-sidebar .bg-blue-500 *, .app-sidebar .bg-blue-600 *,
      .sidebar [class*="bg-blue-500"] *, .sidebar [class*="bg-blue-600"] *,
      .app-sidebar [class*="bg-blue-500"] *, .app-sidebar [class*="bg-blue-600"] *,
      .sidebar [style*="#3b82f6"] *, .sidebar [style*="#2563eb"] *,
      .app-sidebar [style*="#3b82f6"] *, .app-sidebar [style*="#2563eb"] *,
      .sidebar [style*="background-color: var(--accent-color)"] *, .app-sidebar [style*="background-color: var(--accent-color)"] * {
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
        opacity: 1 !important;
      }

      /* Profile avatar / icon in sidebar: use logout bg (blue-500) with white icon/text */
      .sidebar .profile .avatar, .sidebar .profile .icon, .sidebar .profile svg,
      .app-sidebar .profile .avatar, .app-sidebar .profile .icon, .app-sidebar .profile svg {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        fill: #ffffff !important;
        stroke: #ffffff !important;
      }

      /* Logout button style: blue-500 bg with white text */
      .sidebar .logout-button, .app-sidebar .logout-button,
      .sidebar .profile-action, .sidebar .logout, .app-sidebar .logout,
      .sidebar .logout-btn, .app-sidebar .logout-btn {
        background-color: var(--logout-bg) !important;
        color: #ffffff !important;
        border-radius: 4px !important;
        padding: 6px 10px !important;
      }

      /* Ensure non-active sidebar items remain white with dark text */
      .sidebar .menu-item, .app-sidebar .menu-item, .sidebar .nav-link, .app-sidebar .nav-link {
        background-color: #ffffff !important;
        color: var(--sidebar-text) !important;
      }

      /* Make sure hover still looks okay */
      .sidebar a:hover, .app-sidebar a:hover { background-color: rgba(0,0,0,0.03) !important; }
    `;

    document.head.appendChild(style);

    return () => {
      const el = document.getElementById(styleId);
      if (el) el.remove();
    };
  }, [pageTheme]);

  /* ---------- State ---------- */
  const [searchTerm, setSearchTerm] = useState('');
  const [status, setStatus] = useState('All');
  const [deliveryNotes, setDeliveryNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const navigate = useNavigate();

  const [selectedDeliveryNote, setSelectedDeliveryNote] = useState(null);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const [isPDFDesignerOpen, setIsPDFDesignerOpen] = useState(false);
  const [selectedDeliveryNoteForPDF, setSelectedDeliveryNoteForPDF] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true);

  const [stats, setStats] = useState({
    totalDeliveryNotes: 0,
    activeDeliveryNotes: 0,
    inactiveDeliveryNotes: 0,
    createdThisMonth: 0,
    totalAmount: 0,
    amountPaid: 0,
    balanceDue: 0,
    totalTax: 0,
  });

  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });

  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;

  const { hasPermission, hasFullAccess, loading: permissionsLoading } = useScreenPermissions();

  /* ---------- Fetching ---------- */
  useEffect(() => {
    fetchDeliveryNotes();
  }, []);
  

  const fetchDeliveryNotes = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_URL}/delivery-note`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!response.ok) throw new Error('Failed to fetch delivery notes');
      const data = await response.json();
      setDeliveryNotes(data);
      calculateStats(data);
    } catch (err) {
      console.error(err);
      alert('Error loading delivery notes');
    } finally {
      setLoading(false);
    }
  };
   const handleLogout = () => {
    navigate('/login');
  };

  const calculateStats = (deliveryNotesList) => {
    const now = new Date();
    const curMonth = now.getMonth();
    const curYear = now.getFullYear();

    let totalDeliveryNotes = deliveryNotesList.length;
    let activeDeliveryNotes = 0;
    let inactiveDeliveryNotes = 0;
    let createdThisMonth = 0;
    let totalAmount = 0;
    let amountPaid = 0;
    let balanceDue = 0;
    let totalTax = 0;

    deliveryNotesList.forEach((dn) => {
      if (dn.IsActive === 1 || dn.IsActive === true) activeDeliveryNotes++;
      else inactiveDeliveryNotes++;

      const d = new Date(dn.DeliveryNoteDate);
      if (d.getMonth() === curMonth && d.getFullYear() === curYear) createdThisMonth++;

      totalAmount += parseFloat(dn.TotalAmount) || 0;
      amountPaid += parseFloat(dn.AmountPaid) || 0;
      balanceDue += parseFloat(dn.BalanceDue) || 0;
      totalTax += parseFloat(dn.TotalTax) || 0;
    });

    setStats({
      totalDeliveryNotes,
      activeDeliveryNotes,
      inactiveDeliveryNotes,
      createdThisMonth,
      totalAmount,
      amountPaid,
      balanceDue,
      totalTax,
    });
  };

  /* ---------- CSV Export ---------- */
  const handleDownloadCSV = () => {
    const headers =
      'Delivery Note No,Sales Order No,Customer Name,Delivery Note Date,Due Date,Total Amount,Total Tax,Balance Due,Status\n';
    const rows = deliveryNotes.map((deliveryNote) => {
      const salesOrderNo = deliveryNote.Items && deliveryNote.Items.length > 0 ? deliveryNote.Items[0].SalesOrderNumber : '-';
      const statusText = deliveryNote.IsActive === 1 || deliveryNote.IsActive === true ? 'Active' : 'Inactive';
      return [
        deliveryNote.DeliveryNoteNo,
        salesOrderNo,
        deliveryNote.customer_name || '-',
        deliveryNote.DeliveryNoteDate,
        deliveryNote.DueDate,
        deliveryNote.TotalAmount,
        deliveryNote.TotalTax || 0,
        deliveryNote.BalanceDue,
        statusText,
      ].join(',');
    });
    const csv = [headers, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'delivery-notes.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  /* ---------- PDF Download ---------- */
  const downloadDeliveryNotePDF = async (DeliveryNoteNo) => {
  try {
    const response = await axios.get(`${API_URL}/designs/DeliveryNote/pdf/delivery-note/${DeliveryNoteNo}`, {
      responseType: 'arraybuffer'
    });

    const blob = new Blob([response.data], { type: 'application/pdf' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = `DN_${DeliveryNoteNo}.pdf`;
    link.click();

    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    alert(`Failed to download PDF: ${error.message}`);
  }
};

  /* ---------- CRUD & actions ---------- */
  const handleCreate = () => {
    setSelectedDeliveryNote(null);
    setShowModal(true);
  };
  const handleEdit = (dn) => {
    setSelectedDeliveryNote(dn);
    setShowModal(true);
  };
  const handleView = (dn) => {
    setSelectedDeliveryNote(dn);
    setViewModalOpen(true);
  };
  const handleDelete = async (dnNo) => {
    if (!window.confirm(`Delete Delivery Note ${dnNo}?`)) return;
    try {
      const res = await fetch(`${API_URL}/delivery-note/${dnNo}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      if (!res.ok) throw new Error('Delete failed');
      fetchDeliveryNotes();
      alert('Deleted');
    } catch (err) {
      alert('Cannot delete delivery note');
    }
  };
  const toggleDeliveryNoteStatus = async (dnNo, curActive) => {
    try {
      const newActive = curActive ? 0 : 1;
      const payload = { ...selectedDeliveryNote, IsActive: newActive };
      const res = await fetch(`${API_URL}/delivery-note/${dnNo}`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      if (!res.ok) throw new Error('Update failed');
      fetchDeliveryNotes();
    } catch (err) {
      alert('Status update failed');
    }
  };
  const handleModalSubmit = () => fetchDeliveryNotes();

  /* ---------- Sorting ---------- */
  const handleSort = (key) => {
    const dir = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction: dir });
  };

  const getSortedData = (data) => {
    if (!sortConfig.key) return data;
    return [...data].sort((a, b) => {
      let aVal, bVal;
      switch (sortConfig.key) {
        case 'DeliveryNoteNo':
        case 'customer_name':
          aVal = a[sortConfig.key] || '';
          bVal = b[sortConfig.key] || '';
          break;
        case 'DeliveryNoteDate':
        case 'DueDate':
          aVal = a[sortConfig.key] ? new Date(a[sortConfig.key]) : new Date(0);
          bVal = b[sortConfig.key] ? new Date(b[sortConfig.key]) : new Date(0);
          break;
        case 'TotalAmount':
        case 'TotalTax':
        case 'BalanceDue':
          aVal = parseFloat(a[sortConfig.key]) || 0;
          bVal = parseFloat(b[sortConfig.key]) || 0;
          break;
        case 'SalesOrderNo':
          aVal = a.Items && a.Items.length > 0 ? a.Items[0].SalesOrderNumber : '';
          bVal = b.Items && b.Items.length > 0 ? b.Items[0].SalesOrderNumber : '';
          break;
        case 'IsActive':
          aVal = a.IsActive ? 1 : 0;
          bVal = b.IsActive ? 1 : 0;
          break;
        default:
          aVal = a[sortConfig.key];
          bVal = b[sortConfig.key];
      }
      if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  };

  const renderSortArrows = (col) => {
    const active = sortConfig.key === col;
    return (
      <span className="ml-1 inline-flex flex-col items-center">
        <FaSortUp className={`h-3 w-3 ${active && sortConfig.direction === 'asc' ? 'text-slate-900' : 'text-slate-400'}`} />
        <FaSortDown className={`h-3 w-3 -mt-1 ${active && sortConfig.direction === 'desc' ? 'text-slate-900' : 'text-slate-400'}`} />
      </span>
    );
  };

  /* ---------- Filtering & Pagination ---------- */
  const filteredDeliveryNotes = useMemo(() => {
    return deliveryNotes.filter((dn) => {
      const matchesSearch =
        dn.DeliveryNoteNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (dn.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ?? false);
      const active = dn.IsActive === 1 || dn.IsActive === true;
      const matchesStatus = status === 'All' || (status === 'Active' && active) || (status === 'Inactive' && !active);
      return matchesSearch && matchesStatus;
    });
  }, [deliveryNotes, searchTerm, status]);

  const sortedDeliveryNotes = useMemo(() => getSortedData(filteredDeliveryNotes), [filteredDeliveryNotes, sortConfig]);
  const totalPages = Math.ceil(sortedDeliveryNotes.length / itemsPerPage);
  const currentItems = sortedDeliveryNotes.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  const handlePageChange = (page) => setCurrentPage(page);

  const renderPaginationButtons = () => {
    const buttons = [];
    const start = Math.max(1, currentPage - 2);
    const end = Math.min(totalPages, currentPage + 2);

    buttons.push(
      <button
        key="prev"
        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
        disabled={currentPage === 1}
        className={`p-2 transition-all duration-200 ${currentPage === 1 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 hover:bg-slate-100'}`}
        aria-label="Previous page"
      >
        <FaChevronLeft className="w-4 h-4" />
      </button>
    );

    for (let i = start; i <= end; i++) {
      buttons.push(
        <button
          key={i}
          onClick={() => handlePageChange(i)}
          className={`px-3 py-1 transition ${i === currentPage ? 'bg-blue-500 text-white font-semibold' : 'text-slate-800 hover:bg-slate-100'}`}
          aria-current={i === currentPage ? 'page' : undefined}
        >
          {i}
        </button>
      );
    }

    buttons.push(
      <button
        key="next"
        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
        disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 transition-all duration-200 ${currentPage === totalPages || totalPages === 0 ? 'text-slate-300 cursor-not-allowed' : 'text-slate-800 hover:bg-slate-100'}`}
        aria-label="Next page"
      >
        <FaChevronRight className="w-4 h-4" />
      </button>
    );

    return buttons;
  };

  /* ---------- Permissions ---------- */
  const canCreate = hasFullAccess || hasPermission('DELIVERY_NOTE', 'CanCreate');
  const canView = hasFullAccess || hasPermission('DELIVERY_NOTE', 'CanView');
  const canEdit = hasFullAccess || hasPermission('DELIVERY_NOTE', 'CanEdit');
  const canDelete = hasFullAccess || hasPermission('DELIVERY_NOTE', 'CanDelete');

  if (permissionsLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: pageTheme.bg }}>
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-t-4 border-b-4 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-lg font-medium text-gray-700">Loading permissions...</p>
        </div>
      </div>
    );
  }

  /* ---------- Render ---------- */
  return (
    <div
      className="min-h-screen flex"
      style={{
        backgroundColor: pageTheme.bg,
        '--accent-color': pageTheme.accent,
        '--accent-hover': pageTheme.hoverBg,
        '--sidebar-bg': pageTheme.sidebarBg,
        '--page-border-color': pageTheme.border,
        '--page-background': pageTheme.bg,
        '--logout-bg': pageTheme.logoutBg,
      }}
    >
      <Sidebar
        collapsed={sidebarCollapsed}
        onToggle={setSidebarCollapsed}
        theme={{ ...pageTheme, activeBg: pageTheme.accent, activeText: '#ffffff', profileBg: pageTheme.logoutBg, logoutBg: pageTheme.logoutBg }}
        onLogout={handleLogout}
      />

      <main className={`flex-1 p-6 lg:p-8 overflow-hidden  transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-[90px]' : 'lg:ml-[280px]'}`}>
        {/* Header */}
        <header className="mb-8 flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b" style={{ borderColor: pageTheme.border, paddingBottom: 16 }}>
          <div className="flex-1">
            <h1 className="text-3xl font-bold" style={{ color: pageTheme.text }}>Delivery Note</h1>
            <p className="mt-1 text-base" style={{ color: pageTheme.muted }}>Manage your delivery notes here.</p>
          </div>

          <div className="flex items-center gap-3">
            <button onClick={handleDownloadCSV} className="flex items-center gap-2 px-5 py-2 bg-white border font-medium transition-all duration-200 shadow-sm" style={{ color: pageTheme.text, borderColor: pageTheme.border }}>
              <FaDownload className="h-4 w-4" />
              Export
            </button>

            {canCreate && (
              <button onClick={handleCreate} className="flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm bg-blue-500 text-white hover:bg-blue-400" >
                <FaPlus className="h-4 w-4" />
                Create New
              </button>
            )}

            <button
              onClick={() => {
                if (currentItems[0]) {
                  setSelectedDeliveryNoteForPDF(currentItems[0]);
                  setIsPDFDesignerOpen(true);
                }
              }}
              className="flex items-center gap-2 px-5 py-2 font-medium transition-all duration-200 shadow-sm  bg-blue-500 text-white hover:bg-blue-400"
            >
              <FaEdit className="h-4 w-4" />
              Design PDF
            </button>
          </div>
        </header>

        

        {/* Stats Cards */}
        <section className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Delivery Notes', value: stats.totalDeliveryNotes, icon: 'document', color: 'blue' },
            { label: 'Active Delivery Notes', value: stats.activeDeliveryNotes, icon: 'check', color: 'green' },
            { label: 'Inactive Delivery Notes', value: stats.inactiveDeliveryNotes, icon: 'clock', color: 'amber' },
            { label: 'Created This Month', value: stats.createdThisMonth, icon: 'calendar', color: 'purple' },
          ].map((stat) => (
            <div key={stat.label} className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: pageTheme.border }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold" style={{ color: pageTheme.muted }}>{stat.label}</p>
                  <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{stat.value}</p>
                </div>
                <div
                  className={`w-10 h-10 flex items-center justify-center rounded-full`}
                  style={{
                    backgroundColor: stat.color === 'blue' ? '#e6f0ff' : stat.color === 'green' ? '#ecfdf5' : stat.color === 'amber' ? '#fff7ed' : '#f3e8ff',
                    color: stat.color === 'blue' ? '#2563eb' : stat.color === 'green' ? '#16a34a' : stat.color === 'amber' ? '#b45309' : '#7c3aed'
                  }}
                >
                  {stat.icon === 'document' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 4H7a2 2 0 01-2-2V4a2 2 0 012-2h10a2 2 0 012 2v14a2 2 0 01-2 2z" />
                    </svg>
                  )}
                  {stat.icon === 'check' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                  {stat.icon === 'clock' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  )}
                  {stat.icon === 'calendar' && (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                    </svg>
                  )}
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Financial Stats (keeps the same style) */}
        <section className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {[
            { label: 'Total Amount', value: stats.totalAmount.toFixed(2), bg: 'indigo-50', color: '#3730a3' },
            { label: 'Total Tax', value: stats.totalTax.toFixed(2), bg: 'amber-50', color: '#92400e' },
            { label: 'Amount Paid', value: stats.amountPaid.toFixed(2), bg: 'teal-50', color: '#0f766e' },
            { label: 'Balance Due', value: stats.balanceDue.toFixed(2), bg: 'rose-50', color: '#be123c' },
          ].map((s) => (
            <div key={s.label} className="p-5 bg-white border shadow-md transition-shadow duration-200" style={{ borderColor: pageTheme.border }}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold" style={{ color: pageTheme.muted }}>{s.label}</p>
                  <p className="text-2xl font-bold" style={{ color: pageTheme.text }}>{s.value}</p>
                </div>
                <div className="w-10 h-10 flex items-center justify-center rounded-full" style={{ backgroundColor: '#f3f4f6', color: s.color }}>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2" />
                  </svg>
                </div>
              </div>
            </div>
          ))}
        </section>

        {/* Search & Filter */}
        <section className="p-5 bg-white border mb-6" style={{ borderColor: pageTheme.border }}>
          <div className="flex flex-col md:flex-row gap-3 items-end">
            <div className="flex-1">
              <label className="block text-sm font-semibold mb-1" style={{ color: pageTheme.muted }}>Search</label>
              <input
                type="text"
                placeholder="Search delivery note number, customer, etc."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: pageTheme.border }}
              />
            </div>
            <div>
              <label className="block text-sm font-semibold mb-1" style={{ color: pageTheme.muted }}>Status</label>
              <select
                value={status}
                onChange={(e) => setStatus(e.target.value)}
                className="px-4 py-2 bg-white border text-gray-900 focus:outline-none transition-all duration-200"
                style={{ borderColor: pageTheme.border }}
              >
                <option value="All">All</option>
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>
            <button
              onClick={fetchDeliveryNotes}
              className="px-5 py-2 font-medium transition-all duration-200 shadow-sm  bg-blue-500 text-white hover:bg-blue-400"
            >
              Search
            </button>
          </div>
        </section>

        {/* Table block */}
        <section className="bg-white border" style={{ borderColor: pageTheme.border }}>
          <div className="flex justify-between items-center p-4 border-b" >
            <h2 className="text-xl font-semibold" style={{ color: pageTheme.text }}>Delivery Notes</h2>
            <div className="flex gap-2">{renderPaginationButtons()}</div>
          </div>

          {loading ? (
            <div className="p-8 text-center" style={{ color: pageTheme.muted }}>Loading delivery notes...</div>
          ) : currentItems.length === 0 ? (
            <div className="p-8 text-center" style={{ color: pageTheme.muted }}>No delivery notes found</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-xs font-semibold uppercase text-left bg-gray-200 text-gray-600" style={{ backgroundColor: pageTheme.bg, color: pageTheme.muted,  }}>
                  <tr>
                    {[
                      { key: 'DeliveryNoteNo', label: 'DELIVERY NOTE NO.' },
                      { key: 'SalesOrderNo', label: 'SALES ORDER NO.' },
                      { key: 'customer_name', label: 'CUSTOMER NAME' },
                      { key: 'DeliveryNoteDate', label: 'DELIVERY NOTE DATE' },
                      { key: 'DueDate', label: 'DUE DATE' },
                      { key: 'TotalAmount', label: 'TOTAL AMOUNT' },
                      { key: 'TotalTax', label: 'TOTAL TAX' },
                      { key: 'BalanceDue', label: 'BALANCE DUE' },
                      { key: 'IsActive', label: 'STATUS' },
                      { key: 'actions', label: 'Actions' },
                    ].map((col) => (
                      <th
                        key={col.key}
                        className="px-4 py-3 text-left cursor-pointer transition-colors duration-200"
                        onClick={() => col.key !== 'actions' && handleSort(col.key)}
                        
                        scope="col"
                      >
                        <div className="flex items-center gap-2">
                          <span>{col.label}</span>
                          {col.key !== 'actions' && renderSortArrows(col.key)}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody className="divide-y" style={{ borderColor: pageTheme.border }}>
                  {currentItems.map((deliveryNote) => {
                    const salesOrderNo = deliveryNote.Items && deliveryNote.Items.length > 0 ? deliveryNote.Items[0].SalesOrderNumber : '-';
                    const isActive = deliveryNote.IsActive === 1 || deliveryNote.IsActive === true;
                    const statusText = isActive ? 'Active' : 'Inactive';

                    return (
                      <tr key={deliveryNote.DeliveryNoteNo} className="transition-colors duration-200 bg-white hover:bg-gray-50 text-left">
                        <td className="px-4 py-3 font-medium" style={{ color: pageTheme.text }}>{(deliveryNote.DeliveryNoteNo || '-').toString().toUpperCase()}</td>
                        <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{salesOrderNo}</td>
                        <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{(deliveryNote.customer_name || '-').toString().toUpperCase()}</td>
                        <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{deliveryNote.DeliveryNoteDate ? new Date(deliveryNote.DeliveryNoteDate).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{deliveryNote.DueDate ? new Date(deliveryNote.DueDate).toLocaleDateString() : '-'}</td>
                        <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{(parseFloat(deliveryNote.TotalAmount) || 0).toFixed(2)}</td>
                        <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{(parseFloat(deliveryNote.TotalTax) || 0).toFixed(2)}</td>
                        <td className="px-4 py-3" style={{ color: pageTheme.muted }}>{(parseFloat(deliveryNote.BalanceDue) || 0).toFixed(2)}</td>

                        <td className="px-4 py-3">
                          <span
                            role="button"
                            tabIndex={0}
                            className={`inline-block px-3 py-1 text-xs font-medium cursor-pointer rounded-full transition-colors duration-200 uppercase ${isActive ? 'bg-teal-50 text-teal-700 hover:bg-teal-100 font-bold' : 'bg-amber-50 text-amber-700 hover:bg-amber-200 font-bold'}`}
                            onClick={() => canEdit && toggleDeliveryNoteStatus(deliveryNote.DeliveryNoteNo, isActive)}
                            onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && canEdit && toggleDeliveryNoteStatus(deliveryNote.DeliveryNoteNo, isActive)}
                            title={`Click to ${isActive ? 'deactivate' : 'activate'}`}
                            aria-pressed={isActive}
                          >
                            {statusText}
                          </span>
                        </td>

                        <td className="px-4 py-3">
                          <div className="flex gap-3 justify-center">
                            {canView && (
                              <button onClick={() => handleView(deliveryNote)} title="View" aria-label={`View delivery note ${deliveryNote.DeliveryNoteNo}`} className="transition-colors duration-200" style={{ color: pageTheme.accent }}>
                                <FaEye className="h-4 w-4" />
                              </button>
                            )}
                            {canEdit && (
                              <button onClick={() => handleEdit(deliveryNote)} title="Edit" aria-label={`Edit delivery note ${deliveryNote.DeliveryNoteNo}`} className="transition-colors duration-200" style={{ color: '#16a34a' }}>
                                <FaEdit className="h-4 w-4" />
                              </button>
                            )}
                            {canDelete && (
                              <button onClick={() => handleDelete(deliveryNote.DeliveryNoteNo)} title="Delete" aria-label={`Delete delivery note ${deliveryNote.DeliveryNoteNo}`} className="transition-colors duration-200" style={{ color: '#dc2626' }}>
                                <FaTrash className="h-4 w-4" />
                              </button>
                            )}
                            <button onClick={() => downloadDeliveryNotePDF(deliveryNote.DeliveryNoteNo)} title="Download PDF" aria-label={`Download PDF for delivery note ${deliveryNote.DeliveryNoteNo}`} className="transition-colors duration-200" style={{ color: '#7c3aed' }}>
                              <FaDownload className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>

        {/* Modals */}
        <DeliveryNoteFormModal open={showModal} onClose={() => setShowModal(false)} onSubmit={handleModalSubmit} initialData={selectedDeliveryNote} isEdit={!!selectedDeliveryNote} />
        <DeliveryNoteViewModal open={viewModalOpen} onClose={() => setViewModalOpen(false)} deliveryNoteId={selectedDeliveryNote?.DeliveryNoteNo} />

        {/* PDF Designer Modal */}
        {isPDFDesignerOpen && selectedDeliveryNoteForPDF && (
          <PDFDesigner
            isOpen={isPDFDesignerOpen}
            onClose={() => setIsPDFDesignerOpen(false)}
            entity="DeliveryNote"
            sampleData={{
              DeliveryNoteNo: selectedDeliveryNoteForPDF.DeliveryNoteNo || '-',
              DeliveryNoteDate: selectedDeliveryNoteForPDF.DeliveryNoteDate ? new Date(selectedDeliveryNoteForPDF.DeliveryNoteDate).toLocaleDateString() : '-',
              DueDate: selectedDeliveryNoteForPDF.DueDate ? new Date(selectedDeliveryNoteForPDF.DueDate).toLocaleDateString() : '-',
              BranchCode: selectedDeliveryNoteForPDF.BranchCode || '-',
              BranchName: selectedDeliveryNoteForPDF.BranchName || '-',
              Status: selectedDeliveryNoteForPDF.IsActive ? 'Active' : 'Inactive',
              CustomerCode: selectedDeliveryNoteForPDF.CustomerCode || '-',
              CustomerName: selectedDeliveryNoteForPDF.CustomerName || '-',
              Items: (selectedDeliveryNoteForPDF.Items || []).map((item) => ({
                SalesOrderNumber: item.SalesOrderNumber || '-',
                ProductName: item.ProductName || '-',
                Description: item.Description || '-',
                UOM: item.UOMName || '-',
                Quantity: item.Quantity || 0,
                UnitPrice: item.UnitPrice || 0,
                Discount: item.Discount || 0,
                Tax: item.TaxCode || '-',
                TaxRate: item.TaxPercentage || 0,
                TaxAmount: item.TaxAmount || 0,
                Total: item.Total || 0,
              })),
              Summary: {
                SubTotal: selectedDeliveryNoteForPDF.SubTotal || 0,
                TotalTax: selectedDeliveryNoteForPDF.TotalTax || 0,
                TotalDiscount: selectedDeliveryNoteForPDF.Discount || 0,
                ShippingHandling: selectedDeliveryNoteForPDF.ShippingHandlingCharge || 0,
                TotalAmount: selectedDeliveryNoteForPDF.TotalAmount || 0,
                AmountPaid: selectedDeliveryNoteForPDF.AmountPaid || 0,
                BalanceDue: selectedDeliveryNoteForPDF.BalanceDue || 0,
              },
            }}
            availableFields={{
              Header: ['DeliveryNoteNo', 'DeliveryNoteDate', 'DueDate', 'BranchCode', 'BranchName', 'Status', 'CustomerCode', 'CustomerName'],
              Items: ['SalesOrderNumber', 'ProductName', 'Description', 'UOM', 'Quantity', 'UnitPrice', 'Discount', 'Tax', 'TaxRate', 'TaxAmount', 'Total'],
              Summary: ['SubTotal', 'TotalTax', 'TotalDiscount', 'ShippingHandling', 'TotalAmount', 'AmountPaid', 'BalanceDue'],
            }}
          />
        )}
      </main>
    </div>
  );
};

export default InvoiceItems;